import { useEffect, useRef } from 'react';

export function AuthPageAtmosphere() {
  const atmosphereRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const atmosphere = atmosphereRef.current;
    if (!atmosphere || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const setPosition = (horizontal: number, vertical: number) => {
      atmosphere.style.setProperty('--auth-cool-x', `${(horizontal * 320).toString()}px`);
      atmosphere.style.setProperty('--auth-cool-y', `${(vertical * 220).toString()}px`);
      atmosphere.style.setProperty('--auth-warm-x', `${(horizontal * -400).toString()}px`);
      atmosphere.style.setProperty('--auth-warm-y', `${(vertical * -260).toString()}px`);
      atmosphere.style.setProperty('--auth-deep-x', `${(horizontal * 260).toString()}px`);
      atmosphere.style.setProperty('--auth-deep-y', `${(vertical * -340).toString()}px`);
    };

    const onPointerMove = (event: PointerEvent) => {
      const horizontal = (event.clientX / window.innerWidth - 0.5) * 2;
      const vertical = (event.clientY / window.innerHeight - 0.5) * 2;
      setPosition(horizontal, vertical);
    };
    const resetPosition = () => {
      setPosition(0, 0);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('blur', resetPosition);
    document.documentElement.addEventListener('pointerleave', resetPosition);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('blur', resetPosition);
      document.documentElement.removeEventListener('pointerleave', resetPosition);
    };
  }, []);

  return (
    <div
      ref={atmosphereRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      data-testid="auth-atmosphere"
    >
      <div
        className="absolute inset-0"
        data-testid="auth-color-field"
        style={{
          background:
            'radial-gradient(circle at 50% 42%, color-mix(in srgb, var(--bg-white) 94%, transparent) 0%, transparent 42%), var(--bg-page)',
        }}
      />

      <div
        // The ribbons follow the pointer continuously; a kept layer avoids re-rasterizing the blur.
        // react-doctor-disable-next-line react-doctor/no-permanent-will-change
        className="absolute -top-[22rem] -left-[24rem] h-[44rem] w-[82rem] rounded-[50%] opacity-65 blur-3xl will-change-transform"
        data-auth-ribbon="cool"
        style={{
          background:
            'linear-gradient(100deg, transparent 8%, color-mix(in srgb, var(--chart-1) 42%, transparent) 44%, color-mix(in srgb, var(--chart-3) 26%, transparent) 72%, transparent 94%)',
          transform:
            'translate3d(var(--auth-cool-x, 0px), var(--auth-cool-y, 0px), 0) rotate(-12deg)',
        }}
      />
      <div
        // react-doctor-disable-next-line react-doctor/no-permanent-will-change
        className="absolute top-[18%] -right-[34rem] h-[36rem] w-[78rem] rounded-[50%] opacity-55 blur-3xl will-change-transform"
        data-auth-ribbon="warm"
        style={{
          background:
            'linear-gradient(100deg, transparent 4%, color-mix(in srgb, var(--chart-2) 38%, transparent) 42%, color-mix(in srgb, var(--chart-4) 22%, transparent) 70%, transparent 94%)',
          transform:
            'translate3d(var(--auth-warm-x, 0px), var(--auth-warm-y, 0px), 0) rotate(24deg)',
        }}
      />
      <div
        // react-doctor-disable-next-line react-doctor/no-permanent-will-change
        className="absolute -bottom-[28rem] left-[4%] h-[42rem] w-[88rem] rounded-[50%] opacity-45 blur-3xl will-change-transform"
        data-auth-ribbon="deep"
        style={{
          background:
            'linear-gradient(100deg, transparent 6%, color-mix(in srgb, var(--chart-6) 34%, transparent) 38%, color-mix(in srgb, var(--chart-5) 24%, transparent) 68%, transparent 92%)',
          transform:
            'translate3d(var(--auth-deep-x, 0px), var(--auth-deep-y, 0px), 0) rotate(-8deg)',
        }}
      />

      <div
        className="absolute inset-0 opacity-35"
        style={{
          backgroundImage:
            'repeating-linear-gradient(118deg, transparent 0, transparent 54px, color-mix(in srgb, var(--text-primary) 7%, transparent) 55px, transparent 56px)',
          maskImage: 'linear-gradient(to bottom right, black, transparent 42%, black)',
        }}
      />
    </div>
  );
}
